/**
 * The admin portal's AI tab (WP1.1, extended in WP1.2): kill switches, provider profiles,
 * task-class assignments, a test call per class, the usage summary — and the semantic index.
 * API keys never appear here — profiles carry the NAME of an env var; whether it resolves on
 * the server shows as a badge.
 *
 * The index panel reports state, not intent: pages behind their content, chunks with a vector,
 * and which embedding models wrote them. Two models means two vector spaces and half an index.
 */

import { useCallback, useEffect, useState } from "react";

import type { AiOverview, AiProfile } from "../../api";
import { api, ApiError } from "../../api";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Badge, Button, Card, Field, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";

const TASK_CLASSES = [
  { id: "chat", label: "Chat" },
  { id: "extraction", label: "Extraktion" },
  { id: "vision", label: "Vision/OCR" },
  { id: "embedding", label: "Embedding" },
] as const;

const JURISDICTION_LABELS: Record<string, string> = {
  house: "I huset",
  eu: "Inom EU",
  outside_eu: "Utanför EU",
};

const EMPTY_FORM = {
  name: "",
  kind: "local" as "local" | "cloud",
  // Never defaulted for a cloud profile — the API refuses one that has not chosen.
  jurisdiction: "house" as "house" | "eu" | "outside_eu",
  base_url: "",
  model: "",
  api_key_env: "",
};

export function AiSection() {
  const showToast = useToast();
  const [overview, setOverview] = useState<AiOverview | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<AiProfile | "new" | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [testing, setTesting] = useState<string | null>(null);
  const [indexing, setIndexing] = useState(false);

  const errorToast = useCallback(
    (error: unknown, fallback: string) => {
      showToast({ message: error instanceof ApiError ? error.message : fallback, tone: "fail" });
    },
    [showToast],
  );

  const reload = useCallback(() => {
    void api
      .aiOverview()
      .then((data) => {
        setOverview(data);
        setFailed(false);
      })
      .catch((error) => {
        errorToast(error, "AI-översikten kunde inte hämtas.");
        setFailed(true);
      });
  }, [errorToast]);

  useEffect(() => reload(), [reload]);

  const runIndex = async () => {
    setIndexing(true);
    try {
      const result = await api.aiIndexRun();
      showToast({
        message: result.note
          ? result.note
          : `${result.pages} sidor omindexerade, ${result.embedded} stycken fick vektor` +
            (result.remaining > 0 ? ` — ${result.remaining} kvar` : "."),
        tone: result.note ? "fail" : "pass",
      });
      reload();
    } catch (error) {
      errorToast(error, "Indexeringen kunde inte köras.");
    } finally {
      setIndexing(false);
    }
  };

  const rebuildIndex = async (rechunk: boolean) => {
    setIndexing(true);
    try {
      const result = await api.aiIndexRebuild(rechunk);
      showToast({
        message: rechunk
          ? `${result.pages_marked} sidor ska chunkas om och ${result.embeddings_dropped} vektorer slängdes.`
          : `${result.embeddings_dropped} vektorer slängdes — de räknas om vid nästa körning.`,
        tone: "pass",
      });
      reload();
    } catch (error) {
      errorToast(error, "Indexet kunde inte byggas om.");
    } finally {
      setIndexing(false);
    }
  };

  const setSwitch = async (
    name: "ai_enabled" | "cloud_enabled" | "allow_outside_eu",
    value: boolean,
  ) => {
    try {
      const switches = await api.aiSwitches({ [name]: value });
      setOverview((current) => (current ? { ...current, ...switches } : current));
      const label =
        name === "ai_enabled"
          ? value
            ? "AI är på."
            : "AI är avstängt — alla anrop nekas."
          : value
            ? "Molnprofiler får användas för icke-känslig data."
            : "Molnet är avstängt — allt går lokalt.";
      showToast({
        message: label,
        undo: async () => {
          const reverted = await api.aiSwitches({ [name]: !value });
          setOverview((current) => (current ? { ...current, ...reverted } : current));
        },
      });
    } catch (error) {
      errorToast(error, "Inställningen kunde inte sparas.");
    }
  };

  const assign = async (taskClass: string, profileId: string | null) => {
    try {
      await api.aiAssign(taskClass, profileId);
      setOverview((current) =>
        current
          ? { ...current, assignments: { ...current.assignments, [taskClass]: profileId } }
          : current,
      );
    } catch (error) {
      errorToast(error, "Tilldelningen kunde inte sparas.");
    }
  };

  const test = async (taskClass: string) => {
    setTesting(taskClass);
    try {
      const result = await api.aiTest(taskClass);
      showToast({
        message:
          result.status === "ok"
            ? `${taskClass}: ${result.profile_name} (${result.model}) svarade på ${result.latency_ms} ms${result.fallback ? " — via lokal fallback" : ""}.`
            : result.message,
        tone: result.status === "ok" ? "pass" : "warn",
        durationMs: 8000,
      });
    } catch (error) {
      errorToast(error, "Provanropet misslyckades.");
    } finally {
      setTesting(null);
    }
  };

  const openEditor = (profile: AiProfile | "new") => {
    setEditing(profile);
    setForm(
      profile === "new"
        ? EMPTY_FORM
        : {
            name: profile.name,
            kind: profile.kind,
            jurisdiction: profile.jurisdiction,
            base_url: profile.base_url,
            model: profile.model,
            api_key_env: profile.api_key_env,
          },
    );
  };

  const saveProfile = async () => {
    try {
      if (editing === "new") {
        await api.aiCreateProfile(form);
        showToast({ message: `Profilen ”${form.name}” skapades.`, tone: "pass" });
      } else if (editing) {
        await api.aiUpdateProfile(editing.id, form);
        showToast({ message: `Profilen ”${form.name}” uppdaterades.`, tone: "pass" });
      }
      setEditing(null);
      reload();
    } catch (error) {
      errorToast(error, "Profilen kunde inte sparas.");
    }
  };

  const removeProfile = async (profile: AiProfile) => {
    try {
      await api.aiRemoveProfile(profile.id);
      showToast({ message: `Profilen ”${profile.name}” togs bort.`, tone: "pass" });
      reload();
    } catch (error) {
      errorToast(error, "Profilen kunde inte tas bort.");
    }
  };

  if (failed) {
    return <EmptyState title="Ingen AI-översikt" description="Hämtningen misslyckades — försök igen om en stund." />;
  }
  if (overview === null) return <SkeletonText lines={5} />;

  const formValid = form.name.trim() && form.base_url.trim() && form.model.trim();

  return (
    <div className="grid gap-s4">
      <Card className="grid gap-s4">
        <span className="nb-overline">Läge och strömbrytare</span>
        <div className="flex flex-wrap items-center gap-s4">
          <span className="nb-body-muted">
            Router: <strong>{overview.mode === "stub" ? "stub (inspelade svar)" : "live"}</strong>
          </span>
          <Badge status={overview.ai_enabled ? "pass" : "fail"}>
            {overview.ai_enabled ? "AI på" : "AI av"}
          </Badge>
          <Button
            variant={overview.ai_enabled ? "danger" : "default"}
            onClick={() => void setSwitch("ai_enabled", !overview.ai_enabled)}
          >
            {overview.ai_enabled ? "Stäng av AI" : "Slå på AI"}
          </Button>
          <Badge status={overview.cloud_enabled ? "warn" : "pass"}>
            {overview.cloud_enabled ? "Moln på" : "Moln av"}
          </Badge>
          <Button variant="ghost" onClick={() => void setSwitch("cloud_enabled", !overview.cloud_enabled)}>
            {overview.cloud_enabled ? "Stäng av molnet" : "Tillåt moln"}
          </Button>
          <Badge status={overview.allow_outside_eu ? "warn" : "pass"}>
            {overview.allow_outside_eu ? "Utanför EU tillåtet" : "Stannar inom EU"}
          </Badge>
          <Button
            variant="ghost"
            onClick={() => void setSwitch("allow_outside_eu", !overview.allow_outside_eu)}
          >
            {overview.allow_outside_eu ? "Kräv EU" : "Tillåt utanför EU"}
          </Button>
        </div>
        <p className="nb-body-muted">
          Privata utrymmen och känslighetsmärkta sidor går aldrig till molnet, oavsett
          inställningarna ovan — spärren sitter i servern. Den andra, smalare brytaren gäller
          molnprofiler utanför EU: står den på <em>Stannar inom EU</em> faller anropet tillbaka
          på en profil i huset eller inom EU, annars nekas det. Läget (stub/live) styrs av
          MODEL_ROUTER_MODE i .env på servern.
        </p>
      </Card>

      <Card className="grid gap-s4">
        <span className="nb-overline">Uppgiftsklasser</span>
        <ul className="grid gap-s2">
          {TASK_CLASSES.map((taskClass) => (
            <li
              key={taskClass.id}
              className="flex flex-wrap items-center justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
            >
              <span className="nb-body">{taskClass.label}</span>
              <div className="flex items-center gap-s3">
                <select
                  className="nb-field"
                  value={overview.assignments[taskClass.id] ?? ""}
                  onChange={(event) => void assign(taskClass.id, event.target.value || null)}
                >
                  <option value="">— ingen (avstängd) —</option>
                  {overview.profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name} ({profile.kind === "local" ? "lokal" : "moln"})
                    </option>
                  ))}
                </select>
                <Button
                  variant="ghost"
                  disabled={testing === taskClass.id}
                  onClick={() => void test(taskClass.id)}
                >
                  {testing === taskClass.id ? "Provar…" : "Prova"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="grid gap-s4">
        <div className="flex items-center justify-between gap-s3">
          <span className="nb-overline border-0 pb-0">Leverantörsprofiler</span>
          <Button variant="primary" onClick={() => openEditor("new")}>
            Ny profil
          </Button>
        </div>
        {overview.profiles.length === 0 ? (
          <EmptyState
            title="Inga profiler ännu"
            description="Skapa en lokal profil för Sparken (Ollama) eller en molnprofil. Allt talar OpenAI-protokollet."
          />
        ) : (
          <ul className="grid gap-s2">
            {overview.profiles.map((profile) => (
              <li
                key={profile.id}
                className="flex flex-wrap items-center justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
              >
                <div className="grid gap-s1">
                  <span className="nb-body">
                    {profile.name} <span className="nb-body-muted">— {profile.model}</span>
                  </span>
                  <span className="nb-body-muted">{profile.base_url}</span>
                </div>
                <div className="flex items-center gap-s3">
                  <Badge status={profile.kind === "local" ? "pass" : "warn"}>
                    {profile.kind === "local" ? "Lokal" : "Moln"}
                  </Badge>
                  <Badge status={profile.jurisdiction === "outside_eu" ? "warn" : "pass"}>
                    {JURISDICTION_LABELS[profile.jurisdiction] ?? profile.jurisdiction}
                  </Badge>
                  {profile.api_key_env && (
                    <Badge status={profile.key_present ? "pass" : "fail"}>
                      {profile.key_present ? "Nyckel finns" : `${profile.api_key_env} saknas`}
                    </Badge>
                  )}
                  <Button variant="ghost" onClick={() => openEditor(profile)}>
                    Ändra
                  </Button>
                  <Button variant="danger" onClick={() => void removeProfile(profile)}>
                    Ta bort
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="grid gap-s4">
        <span className="nb-overline">Semantiskt index</span>
        <ul className="grid gap-s2">
          <li className="flex flex-wrap justify-between gap-s3 border-b border-line pb-s2">
            <span className="nb-body">Sidor i indexet</span>
            <span className="nb-body-muted">
              <Num>{overview.index.pages_indexed}</Num> aktuella ·{" "}
              <Num>{overview.index.pages_pending}</Num> väntar
            </span>
          </li>
          <li className="flex flex-wrap justify-between gap-s3 border-b border-line pb-s2">
            <span className="nb-body">Stycken</span>
            <span className="nb-body-muted">
              <Num>{overview.index.chunks_embedded}</Num> av <Num>{overview.index.chunks}</Num> har
              vektor
            </span>
          </li>
          <li className="flex flex-wrap justify-between gap-s3">
            <span className="nb-body">Modell</span>
            <span className="nb-body-muted">
              {overview.index.models.length === 0
                ? "ingen ännu"
                : overview.index.models
                    .map((row) => `${row.model} (${row.chunks})`)
                    .join(" · ")}
            </span>
          </li>
        </ul>
        {overview.index.models.length > 1 && (
          <p className="nb-body">
            Indexet är byggt med mer än en modell. Vektorer från olika modeller går inte att
            jämföra, så bara en del av indexet svarar. Kör om indexet.
          </p>
        )}
        <div className="flex flex-wrap gap-s3">
          <Button variant="ghost" disabled={indexing} onClick={() => void runIndex()}>
            {indexing ? "Indexerar …" : "Indexera nu"}
          </Button>
          <Button variant="ghost" disabled={indexing} onClick={() => void rebuildIndex(false)}>
            Räkna om vektorer
          </Button>
          <Button variant="ghost" disabled={indexing} onClick={() => void rebuildIndex(true)}>
            Bygg om från grunden
          </Button>
        </div>
        <p className="nb-body-muted">
          Indexeringen sker annars av sig själv varje minut. Innehållet rörs aldrig — indexet är
          härledd data och går alltid att bygga om.
        </p>
      </Card>

      <Card className="grid gap-s4">
        <span className="nb-overline">Förbrukning senaste 7 dygnen</span>
        {overview.usage.length === 0 ? (
          <p className="nb-body-muted">Inga anrop ännu.</p>
        ) : (
          <ul className="grid gap-s2">
            {overview.usage.map((row) => (
              <li
                key={row.task_class}
                className="flex flex-wrap justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
              >
                <span className="nb-body">{row.task_class}</span>
                <span className="nb-body-muted">
                  <Num>{row.calls}</Num> anrop · <Num>{row.tokens_in + row.tokens_out}</Num> tokens ·{" "}
                  <Num>{row.cost.toFixed(2)}</Num> kr
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog
        open={editing !== null}
        title={editing === "new" ? "Ny leverantörsprofil" : "Ändra profilen"}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Avbryt
            </Button>
            <Button variant="primary" disabled={!formValid} onClick={() => void saveProfile()}>
              Spara
            </Button>
          </>
        }
      >
        <div className="grid gap-s4">
          <Field
            label="Namn"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <label className="grid gap-s2">
            <span className="nb-overline border-0 pb-0">Typ</span>
            <select
              className="nb-field"
              value={form.kind}
              onChange={(event) => {
                const kind = event.target.value as "local" | "cloud";
                // The two belong together: a local profile is in the house by definition, and a
                // cloud profile has to state where it lives before it can be saved.
                setForm({
                  ...form,
                  kind,
                  jurisdiction: kind === "local" ? "house" : form.jurisdiction === "house" ? "eu" : form.jurisdiction,
                });
              }}
            >
              <option value="local">Lokal (lämnar aldrig huset)</option>
              <option value="cloud">Moln (endast icke-känslig data)</option>
            </select>
          </label>
          {form.kind === "cloud" && (
            <label className="grid gap-s2">
              <span className="nb-overline border-0 pb-0">Var ligger den?</span>
              <select
                className="nb-field"
                value={form.jurisdiction}
                onChange={(event) =>
                  setForm({ ...form, jurisdiction: event.target.value as "eu" | "outside_eu" })
                }
              >
                <option value="eu">Inom EU</option>
                <option value="outside_eu">Utanför EU</option>
              </select>
              <span className="text-small text-muted">
                Avgör spärren, inte namnet på profilen. Utanför EU kräver dessutom att brytaren
                ovan är på.
              </span>
            </label>
          )}
          <Field
            label="Bas-URL (OpenAI-kompatibel)"
            value={form.base_url}
            onChange={(event) => setForm({ ...form, base_url: event.target.value })}
            help="T.ex. http://sparken:11434/v1 eller https://<resurs>.openai.azure.com/openai/v1"
          />
          <Field
            label="Modell"
            value={form.model}
            onChange={(event) => setForm({ ...form, model: event.target.value })}
          />
          <Field
            label="Namn på nyckelvariabel (valfritt)"
            value={form.api_key_env}
            onChange={(event) => setForm({ ...form, api_key_env: event.target.value })}
            help="Namnet på en variabel i serverns .env, t.ex. AZURE_OPENAI_API_KEY. Själva nyckeln skrivs aldrig här."
          />
        </div>
      </Dialog>
    </div>
  );
}
